/**
 * SFD Reader Plugin — extracts sf: namespace properties from parsed BPMN elements.
 * Registered via epxFlow.addReaderExtension(plugin).
 */

// Direct sf: attributes per element type (read from moddle-parsed element)
const SF_ATTRS = [
  // Identity
  'guid', 'code', 'url', 'version',
  // Boundary overdue
  'overdue', 'overdueType',
  // AdminProcessDetail (extends bpmn:Task/UserTask in sfd-v1 and sfd-v2)
  'automaticAssignment', 'approvalRequired', 'sendBackActivity',
  'electDocRequired', 'publishDocument', 'publishAttachments', 'publishRelatedDocument',
  // DocumentManagementDetail
  'assignTo', 'linkDocumentExpedient', 'linkAttachmentExpedient', 'sendersAssignment',
  // MultiSignDetail
  'complexType', 'mergeType', 'compareType', 'completeOrder',
  // GatewayDetail
  'extraSplitType', 'extraJoinType', 'joinPassType',
  // TriggerDetail v1 format (direct attrs via extends in sf.json)
  'trigger', 'expression',
  // TriggerDetail v2 backward compat (old sfd-v2 used these names as direct attrs)
  'triggerType', 'timerExpression', 'conditionExpression', 'messageRef', 'signalRef',
  'expressionText',
  // Section v2 backward compat (old sfd-v2 used sectionText/sectionName as direct attrs)
  'sectionText', 'sectionName',
  // Service/Script v2 backward compat (old sfd-v2 used these as direct attrs)
  'serviceMethodType', 'serviceSubMethodType', 'serviceArgus', 'serviceExpression',
  'scriptType', 'scriptText', 'scriptArgus',
  // BusinessRuleDetail (extends bpmn:BusinessRuleTask)
  'decisionRef', 'resultVariable',
  // SequenceFlow: from/to (TransitionIdentity extends bpmn:SequenceFlow)
  'from', 'to',
  // SequenceFlow: group behaviours (GroupBehaviours extends bpmn:SequenceFlow)
  'priority', 'forcedMerge', 'defaultBranch', 'approval', 'recieverType',
];

// extensionElement $type → flat props to lift into the node's props map
// Handles old sfd-v2 extensionElement format (before matching sfd-v1)
const EXT_FLAT_MAP = {
  // Old sfd-v2 extensionElement names (backward compat)
  'sf:AdminProcessDetail': [
    'automaticAssignment', 'approvalRequired', 'sendBackActivity',
    'electDocRequired', 'publishDocument', 'publishAttachments', 'publishRelatedDocument',
  ],
  'sf:DocumentManagementDetail': [
    'assignTo', 'linkDocumentExpedient', 'linkAttachmentExpedient', 'sendersAssignment',
  ],
  'sf:MultiSignDetail': [
    'complexType', 'mergeType', 'compareType', 'completeOrder',
  ],
  'sf:GatewayDetail': [
    'extraSplitType', 'extraJoinType', 'joinPassType',
  ],
  // Old sfd-v2 extensionElement for GroupBehaviours
  'sf:GroupBehaviours': [
    'priority', 'forcedMerge', 'defaultBranch', 'approval', 'recieverType',
  ],
  // v2 backward compat renames
  'sf:AdminProcess': [
    'automaticAssignment', 'approvalRequired', 'sendBackActivity',
    'electDocRequired', 'publishDocument', 'publishAttachments', 'publishRelatedDocument',
  ],
  'sf:DocumentManagement': [
    'assignTo', 'linkDocumentExpedient', 'linkAttachmentExpedient', 'sendersAssignment',
  ],
  'sf:MultiSign': [
    'complexType', 'mergeType', 'compareType', 'completeOrder',
  ],
};

export class SfdReaderPlugin {
  extractProperties(el, props) {
    // Read direct sf: attributes — only plain primitive values (string/number/boolean).
    for (const attr of SF_ATTRS) {
      const val = el[attr];
      if (val === undefined || val === null || val === '') continue;
      if (typeof val === 'object') continue;
      props[attr] = val;
    }

    // ── Normalize v1 trigger format → internal v2 format ─────────────────
    // sfd-v1 writes: sf:trigger="Timer" sf:expression="PT15M" as direct attrs
    if (props.trigger && !props.triggerType) {
      props.triggerType = props.trigger;
      if (props.expression) {
        const t = props.triggerType;
        if (t === 'Timer') props.timerExpression = props.expression;
        else if (t === 'Condition') props.conditionExpression = props.expression;
        else if (t === 'Message') props.messageRef = props.expression;
        else if (t === 'Signal') props.signalRef = props.expression;
        else props.timerExpression = props.expression; // fallback
      }
      delete props.trigger;
      delete props.expression;
    }

    const extensions = el.extensionElements;
    if (!extensions || !extensions.values) return;

    for (const ext of extensions.values) {
      const type = ext.$type;

      // ── Complex list extensions ──────────────────────────────────────────

      if (type === 'sf:Actions' && ext.actions) {
        props.actions = ext.actions.map((a) => {
          const action = {};
          for (const key of [
            'fireType', 'methodType', 'subMethodType', 'argus', 'expression',
            'emailBody', 'emailCC', 'emailSubject', 'emailTo',
            'typeFullName', 'assembly', 'methodName',
          ]) {
            if (a[key] !== undefined && a[key] !== null) action[key] = a[key];
          }
          return action;
        });
      }

      if (type === 'sf:Performers' && ext.values) {
        props.performers = ext.values.map((p) => {
          const perf = {};
          for (const key of ['name', 'outerId', 'outerCode', 'outerType', 'roleLevel']) {
            if (p[key] !== undefined && p[key] !== null) perf[key] = p[key];
          }
          return perf;
        });
      }

      if (type === 'sf:Notifications' && ext.values) {
        props.notifications = ext.values.map((n) => {
          const notif = {};
          if (n.notificationType) notif.notificationType = n.notificationType;
          // notificationText is isBody in current sf.json; fall back to $attrs for old v2 saves
          const text = n.notificationText || (n.$attrs && n.$attrs.notificationText) || '';
          if (text) notif.notificationText = text;
          return notif;
        });
      }

      if (type === 'sf:Boundaries' && ext.boundaries && !props.boundaries) {
        props.boundaries = ext.boundaries.map((b) => ({
          event: b.event || '',
          expression: b.expression || '',
        }));
      }

      if (type === 'sf:SubInfoes' && ext.values) {
        props.subInfoes = ext.values.map((s) => {
          const info = {};
          for (const key of ['subProcessName', 'subProcessGUID', 'subType', 'subId', 'subVar']) {
            if (s[key] !== undefined && s[key] !== null) info[key] = s[key];
          }
          return info;
        });
      }

      // ── Backward compat: sfd-v1 trigger extensionElement ────────────────
      // sfd-v1 writes: sf:trigger="Timer" sf:expression="PT15M" as DIRECT ATTRS (via extends).
      // The extensionElement form only appears in very old intermediate formats.
      if (type === 'sf:TriggerDetail' && !props.triggerType) {
        if (ext.trigger !== undefined && ext.trigger !== null && ext.trigger !== '') {
          props.triggerType = ext.trigger;
        }
        if (ext.expression !== undefined && ext.expression !== null && ext.expression !== '') {
          const triggerType = props.triggerType || '';
          if (triggerType === 'Timer') props.timerExpression = ext.expression;
          else if (triggerType === 'Condition') props.conditionExpression = ext.expression;
          else if (triggerType === 'Message') props.messageRef = ext.expression;
          else if (triggerType === 'Signal') props.signalRef = ext.expression;
          else props.timerExpression = ext.expression;
        }
      }

      // ── Backward compat: sfd-v1 Boundaries list ─────────────────────────
      if (type === 'sf:Boundaries' && ext.boundaries) {
        props.boundaries = ext.boundaries.map((b) => ({
          event: b.event || '',
          expression: b.expression || '',
        }));
      }

      // ── Backward compat: sfd-v1 Sections ────────────────────────────────
      // sfd-v1: <sf:sections><sf:section name="x">body</sf:section></sf:sections>
      if (type === 'sf:Sections' && ext.sections && ext.sections.length) {
        const first = ext.sections[0];
        if (!props.sectionText) {
          props.sectionText = first.text || first._body || '';
        }
        if (!props.sectionName && first.name) {
          props.sectionName = first.name;
        }
      }

      // ── Backward compat: sfd-v1 Services ────────────────────────────────
      // sfd-v1: <sf:services><sf:service methodType="..." .../></sf:services>
      if (type === 'sf:Services' && ext.services && ext.services.length) {
        const svc = ext.services[0];
        if (!props.serviceMethodType && svc.methodType) props.serviceMethodType = svc.methodType;
        if (!props.serviceSubMethodType && svc.subMethodType) props.serviceSubMethodType = svc.subMethodType;
        if (!props.serviceArgus && svc.argus) props.serviceArgus = svc.argus;
        if (!props.serviceExpression && svc.expression) props.serviceExpression = svc.expression;
      }

      // ── Backward compat: sfd-v1 Scripts ─────────────────────────────────
      // sfd-v1: <sf:scripts><sf:script scriptType="CSharp" argus="...">body</sf:script></sf:scripts>
      if (type === 'sf:Scripts' && ext.scripts && ext.scripts.length) {
        const scr = ext.scripts[0];
        if (!props.scriptType && scr.scriptType) props.scriptType = scr.scriptType;
        if (!props.scriptArgus && scr.argus) props.scriptArgus = scr.argus;
        if (!props.scriptText && (scr.scriptText || scr._body)) props.scriptText = scr.scriptText || scr._body;
      }

      // ── sf:DecisionTable: DMN XML body on BusinessRuleTask ──────────────
      if (type === 'sf:DecisionTable' && !props.dmnXml) {
        const xml = ext.xml || ext._body || (ext.$attrs && ext.$attrs.xml) || '';
        if (xml) props.dmnXml = xml;
      }

      // ── Flat extensionElement attrs → direct props (old sfd-v2 format) ──
      const flatAttrs = EXT_FLAT_MAP[type];
      if (flatAttrs) {
        for (const key of flatAttrs) {
          if (ext[key] !== undefined && ext[key] !== null && ext[key] !== '') {
            if (props[key] === undefined) {
              props[key] = ext[key];
            }
          }
        }
      }
    }
  }

  /**
   * Extracts sf: properties from a bpmn:Process element.
   */
  extractProcessProperties(processEl, processProps) {
    for (const attr of ['guid', 'code', 'url', 'version', 'name']) {
      if (processEl[attr] !== undefined && processEl[attr] !== null && processEl[attr] !== '') {
        processProps[attr] = processEl[attr];
      }
    }
  }
}
